import React from 'react';
import { useTranslation } from 'react-i18next';
import { CloseIcon } from '../common/CloseIcon';

interface ProfileHeaderProps {
  title: string;
  variant?: 'default' | 'newLandingPage';
  /** A subview shows a back control on desktop too; the root screen has nowhere to go back to. */
  showBack?: boolean;
  /** Mobile back arrow. The profile root passes its close; a subview passes its own back. */
  onBack: () => void;
  /** Desktop close. Always leaves the profile entirely, subview or not. */
  onClose: () => void;
}

/**
 * The profile view's header, for the root screen and every subview alike
 * (my listings, buy interests, price estimations, notification settings).
 *
 * Each subview used to draw its own: two of them shared ProfileSubviewHeader
 * and price estimations had a third of its own, so the title moved, the back
 * control changed shape, and no subview offered the desktop close the root had.
 * The container owns the header now and only the title varies.
 */
const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  title,
  variant = 'default',
  showBack = false,
  onBack,
  onClose,
}) => {
  const { t } = useTranslation();

  if (variant !== 'newLandingPage') {
    return (
      <div className="profile-view__header">
        {showBack && (
          <button
            className="profile-view__header__back-button profile-view__header__back-button--desktop"
            onClick={onBack}
            type="button"
            aria-label={t('newLandingPage.profile.backAria')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
              <path d="M11.1512 3.4301C11.558 3.0233 12.2174 3.0233 12.6242 3.4301C13.0307 3.83691 13.0309 4.49637 12.6242 4.90308L6.06899 11.4583H22.3064C22.8817 11.4583 23.348 11.9246 23.348 12.4999C23.3478 13.075 22.8815 13.5416 22.3064 13.5416H6.06899L12.6242 20.0968C13.0307 20.5036 13.0309 21.163 12.6242 21.5697C12.2175 21.9764 11.558 21.9763 11.1512 21.5697L3.1861 13.6047C2.57597 12.9945 2.576 12.0054 3.1861 11.3952L11.1512 3.4301Z" fill="black" />
            </svg>
          </button>
        )}
        <h1 className="profile-view__header__title">{title}</h1>
        <button
          className="profile-view__header__close-button"
          onClick={onClose}
          aria-label={t('newLandingPage.profile.closeAria')}
        >
          <CloseIcon size={18} />
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="profile-view__header profile-view__header--new-landing-page-mobile">
        <button
          className="profile-view__header__back-button"
          onClick={onBack}
          type="button"
          aria-label={t('newLandingPage.profile.backAria')}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
            <path d="M11.1512 3.4301C11.558 3.0233 12.2174 3.0233 12.6242 3.4301C13.0307 3.83691 13.0309 4.49637 12.6242 4.90308L6.06899 11.4583H22.3064C22.8817 11.4583 23.348 11.9246 23.348 12.4999C23.3478 13.075 22.8815 13.5416 22.3064 13.5416H6.06899L12.6242 20.0968C13.0307 20.5036 13.0309 21.163 12.6242 21.5697C12.2175 21.9764 11.558 21.9763 11.1512 21.5697L3.1861 13.6047C2.57597 12.9945 2.576 12.0054 3.1861 11.3952L11.1512 3.4301Z" fill="black" />
          </svg>
        </button>
        <h1 className="profile-view__header__title profile-view__header__title--new-landing-page">
          {title}
        </h1>
        <div
          className="profile-view__header__back-button profile-view__header__back-button--spacer"
          aria-hidden="true"
        />
      </div>
      <div className="profile-view__header profile-view__header--new-landing-page-desktop">
        {showBack && (
          <button
            className="profile-view__header__back-button profile-view__header__back-button--desktop"
            onClick={onBack}
            type="button"
            aria-label={t('newLandingPage.profile.backAria')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
              <path d="M11.1512 3.4301C11.558 3.0233 12.2174 3.0233 12.6242 3.4301C13.0307 3.83691 13.0309 4.49637 12.6242 4.90308L6.06899 11.4583H22.3064C22.8817 11.4583 23.348 11.9246 23.348 12.4999C23.3478 13.075 22.8815 13.5416 22.3064 13.5416H6.06899L12.6242 20.0968C13.0307 20.5036 13.0309 21.163 12.6242 21.5697C12.2175 21.9764 11.558 21.9763 11.1512 21.5697L3.1861 13.6047C2.57597 12.9945 2.576 12.0054 3.1861 11.3952L11.1512 3.4301Z" fill="black" />
            </svg>
          </button>
        )}
        <h1 className="profile-view__header__title">{title}</h1>
        <button
          className="profile-view__header__close-button"
          onClick={onClose}
          aria-label={t('newLandingPage.profile.closeAria')}
        >
          <CloseIcon size={18} />
        </button>
      </div>
    </>
  );
};

export default ProfileHeader;
